import { useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { CardWrapper } from "features/user/cards/CardWrapper"

import { useIsMounted } from "hooks/useIsMounted"
import { useTimezone } from "hooks/useTimezone"

import { UserReload } from "services/user/type"
import { userService } from "services/user/user.service"

import { formatFiatCurrency } from "util/formatter"

interface ActiveReloadCardProps {
  reload: UserReload
  onCancelled: () => void
}

export const ActiveReloadCard = ({ reload, onCancelled }: ActiveReloadCardProps) => {
  const isMounted = useIsMounted()
  const { t } = useTranslation("translation", { useSuspense: false })
  const { formatDateTime } = useTimezone()

  const [isCancelling, setIsCancelling] = useState(false)

  const claimsMade = reload.claims_made
  const total = reload.days

  const handleCancel = async () => {
    setIsCancelling(true)
    const result = await userService.cancelReload(reload.id)
    if (isMounted()) setIsCancelling(false)
    if (result) {
      toast.success(String(t("addUserBonus.reloadActiveCancelSuccess")))
      onCancelled()
    }
  }

  const rows: { label: string; value: string }[] = [
    { label: t("addUserBonus.reloadActiveTotal"), value: formatFiatCurrency(reload.total_amount, "$0.00") },
    { label: t("addUserBonus.reloadActiveDaily"), value: formatFiatCurrency(reload.daily_amount, "$0.00") },
    { label: t("addUserBonus.reloadActiveDuration"), value: t("addUserBonus.reloadActiveDays", { count: reload.days }) },
    {
      label: t("addUserBonus.reloadActiveStarted"),
      value: reload.started_at ? formatDateTime(reload.started_at) : String(t("addUserBonus.reloadActiveNotStarted"))
    },
    { label: t("addUserBonus.reloadActiveExpires"), value: reload.expires_at ? formatDateTime(reload.expires_at) : "—" },
    {
      label: t("addUserBonus.reloadActiveLastClaim"),
      value: reload.last_claimed_at ? formatDateTime(reload.last_claimed_at) : String(t("addUserBonus.reloadActiveNever"))
    }
  ]

  return (
    <div className="flex flex-col gap-4">
      <CardWrapper>
        <div className="flex items-center gap-3 text-black dark:text-white text-lg mb-4">
          <span>{t("addUserBonus.reloadActiveTitle")}</span>
          <span className="badge badge-success">{reload.status}</span>
        </div>

        <div className="flex flex-col gap-3">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex justify-between gap-3">
              <div className="text-base text-black dark:text-[rgba(156,163,175,1)]">{label}</div>
              <div className="text-base text-black dark:text-white text-right">{value}</div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-black dark:text-[rgba(156,163,175,1)]">{t("addUserBonus.reloadActiveProgress")}</span>
            <span className="text-black dark:text-white">{t("addUserBonus.reloadActiveClaims", { claimed: claimsMade, total })}</span>
          </div>
          <progress className="progress progress-primary w-full" value={claimsMade} max={total} />
        </div>
      </CardWrapper>

      <button type="button" className={clsx("btn btn-error", { loading: isCancelling })} onClick={handleCancel} disabled={isCancelling}>
        {t("addUserBonus.reloadActiveCancel")}
      </button>
    </div>
  )
}
